import { IContactCenterTenant } from './types';

/**
 * Resolves a numeric-tenant-id task/task-context to the tenant-level OrgNode
 * Guid the agent-inbox MFEs key scope on. Mirrors the join AuthModule.hasAccessToTenant
 * already does over the user's tenant list — no second lookup path.
 *
 * Absent is a valid result, not an error: a tenant not in the user's list, or
 * a tenant whose orgNodeId hasn't been populated yet, must not fall back to
 * org.id (that substitution is exactly what CCPE-1137 removes).
 */
export function resolveOrgNodeId(
  tenants: ReadonlyArray<IContactCenterTenant>,
  tenantId: string | undefined,
): string | undefined {
  if (!tenantId) {
    return undefined;
  }
  return tenants.find((t) => t.id === tenantId)?.orgNodeId ?? undefined;
}

/**
 * Reverse of resolveOrgNodeId — the agent-inbox MFEs hand back an OrgNode
 * Guid (click-to-call, monolith navigation), but numeric-tenant-id call sites
 * (outbound-call GraphQL mutations, monolith tenant-redirect URLs) still need
 * the numeric id. Out of scope for CCPE-1137: those call sites aren't
 * migrating to the Guid, so this is the bridge back, not a second identifier.
 */
export function resolveTenantId(
  tenants: ReadonlyArray<IContactCenterTenant>,
  orgNodeId: string | undefined,
): string | undefined {
  if (!orgNodeId) {
    return undefined;
  }
  return tenants.find((t) => t.orgNodeId === orgNodeId)?.id ?? undefined;
}

/**
 * Display label for the tenant an OrgNode Guid belongs to — the agent-inbox
 * MFEs take the label as a separate seed from the scope Guid, so the host has
 * to resolve it (CCPE-1656).
 *
 * Prefers companyName over name, matching every other tenant label in the app
 * (useTenantName, TenantSelectionSection, the urgent-followups filter options).
 * `||` rather than `??`, for the same reason those do: companyName is routinely
 * an empty string rather than null, and `??` would let that through as a blank
 * label. Absent is a valid result — a Guid outside the user's tenant list has no
 * label to show, and the MFE falls back to its own default chip rather than
 * rendering a blank one.
 *
 * The lookup is repeated from resolveTenantId rather than shared: resolveTenantId
 * runs on paths that are not behind featureEnableAgentInboxForCalls (Messages, via
 * resolveNavigateUrl), and this ticket keeps its diff inside that flag. Four
 * duplicated lines are the cheaper trade.
 */
export function resolveTenantName(
  tenants: ReadonlyArray<IContactCenterTenant>,
  orgNodeId: string | undefined,
): string | undefined {
  if (!orgNodeId) {
    return undefined;
  }
  const tenant = tenants.find((t) => t.orgNodeId === orgNodeId);
  return tenant?.companyName || tenant?.name || undefined;
}
